import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { defaults, type ContentKey, type SiteContent } from "./defaults";

// DB 콘텐츠를 앱 시작 시 한 번 불러온다. 실패하거나 3초 안에 응답이 없으면 기본값으로 표시.
const ContentContext = createContext<SiteContent>(defaults);

const TIMEOUT_MS = 3000;

export function mergeContent(db: Partial<SiteContent>): SiteContent {
  const out = { ...defaults } as SiteContent;
  for (const key of Object.keys(defaults) as ContentKey[]) {
    const value = db[key];
    if (value === undefined || value === null) continue;
    const base = defaults[key];
    // 객체 항목은 기본값 위에 덮어써서, DB에 없는 새 필드도 기본값으로 채워지게 한다
    (out as Record<string, unknown>)[key] =
      base && typeof base === "object" && !Array.isArray(base) && typeof value === "object" && !Array.isArray(value)
        ? { ...base, ...value }
        : value;
  }
  return out;
}

export function ContentProvider({ children }: { children: ReactNode }) {
  const [content, setContent] = useState<SiteContent | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => setContent((c) => c ?? defaults), TIMEOUT_MS);
    fetch("/api/content.php")
      .then((res) => (res.ok ? res.json() : Promise.reject(res.status)))
      .then((data) => setContent(mergeContent(data.content ?? {})))
      .catch(() => setContent((c) => c ?? defaults))
      .finally(() => clearTimeout(timer));
    return () => clearTimeout(timer);
  }, []);

  if (!content) return null;
  return <ContentContext.Provider value={content}>{children}</ContentContext.Provider>;
}

export function useContent<K extends ContentKey>(key: K): SiteContent[K] {
  return useContext(ContentContext)[key];
}

// "02-002-2525" → "tel:020022525"
export function telHref(phone: string): string {
  return `tel:${phone.replace(/[^0-9+]/g, "")}`;
}
